import Box from '@mui/material/Box';
import {
  GridToolbarColumnsButton,
  GridToolbarContainer,
  QuickFilter,
  QuickFilterClear,
  QuickFilterControl,
  useGridApiContext,
  useGridRootProps,
} from '@mui/x-data-grid-premium';
import { ReactNode, Ref } from 'react';

import { Nullable } from '~types/Nullable';

interface DataGridToolBarProps {
  toolbarSidebarContent?: Nullable<ReactNode>;
  toolbarTrailingContent?: Nullable<ReactNode>;
  hideQuickFilter?: boolean;
}

// MUI X v8's GridToolbarQuickFilter renders collapsed to an icon and only
// expands on click, with no prop to keep it open. Compose the v8 QuickFilter
// primitives with a controlled `expanded` instead, rendering the same
// baseTextField (magnifier + clear adornments) the grid uses, so the search
// box stays permanently expanded like it was on v7.
const AlwaysExpandedQuickFilter = () => {
  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();
  const BaseTextField = rootProps.slots.baseTextField;
  const BaseIconButton = rootProps.slots.baseIconButton;
  const QuickFilterIcon = rootProps.slots.quickFilterIcon;
  const QuickFilterClearIcon = rootProps.slots.quickFilterClearIcon;

  return (
    <QuickFilter expanded debounceMs={1000}>
      <QuickFilterControl
        render={({ ref, slotProps: controlSlotProps, ...controlProps }) => (
          <BaseTextField
            {...controlProps}
            // The grid's own Ref alias is written against React 19 typings and
            // does not unify with React 18's React.Ref; runtime shape is the same.
            inputRef={ref as Ref<HTMLInputElement>}
            aria-label={apiRef.current.getLocaleText('toolbarQuickFilterLabel')}
            placeholder={apiRef.current.getLocaleText('toolbarQuickFilterPlaceholder')}
            size="small"
            // Replicates v7's rendered quick filter: 227px total width, and the
            // magnifier drawn like v7's raw (unwrapped) adornment — the v8
            // material adapter wraps adornments in a gray InputAdornment with
            // an 8px trailing margin, so neutralize both and keep v7's 4px
            // input offset.
            material={{
              sx: {
                // v7 rendered a 16px right margin on the field regardless of
                // whether trailing toolbar content followed it.
                mr: 2,
                width: '227px',
                // v7's quick filter root carried this 4px; it sizes the toolbar
                // row so sibling buttons center 2px lower, exactly as on v7.
                pb: 0.5,
                '& .MuiInputAdornment-positionStart': {
                  color: 'text.primary',
                  marginRight: 0,
                },
                '& input': { ml: 0.5 },
              },
            }}
            slotProps={{
              ...controlSlotProps,
              input: {
                startAdornment: <QuickFilterIcon fontSize="small" />,
                endAdornment: controlProps.value ? (
                  <QuickFilterClear
                    render={
                      <BaseIconButton
                        size="small"
                        edge="end"
                        aria-label={apiRef.current.getLocaleText(
                          'toolbarQuickFilterDeleteIconLabel',
                        )}
                      >
                        <QuickFilterClearIcon fontSize="small" />
                      </BaseIconButton>
                    }
                  />
                ) : null,
                ...controlSlotProps?.input,
              },
            }}
          />
        )}
      />
    </QuickFilter>
  );
};

const DataGridToolBar = ({
  toolbarSidebarContent,
  toolbarTrailingContent,
  hideQuickFilter = false,
}: DataGridToolBarProps) => {
  return (
    <>
      <GridToolbarContainer
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'start',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
            <Box
              sx={{
                order: 1,
                display: 'flex',
                mr: 2,
                ml: 0,
              }}
            >
              {toolbarSidebarContent}
            </Box>
            <Box
              sx={{
                display: 'flex',
                order: 2,
              }}
            >
              <GridToolbarColumnsButton
                slotProps={{
                  // text.primary (not the fixed navy secondary.main) so the label
                  // stays legible in dark mode.
                  button: {
                    material: {
                      sx: { mr: 1, color: 'text.primary' },
                      variant: 'text',
                    },
                  },
                }}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {!hideQuickFilter && <AlwaysExpandedQuickFilter />}
            {/* The quick filter keeps a 4px bottom padding to preserve v7 row
                sizing. Centering splits that between the siblings, sitting them
                2px low, so give trailing content the matching half back. */}
            <Box sx={{ pb: '2px' }}>{toolbarTrailingContent}</Box>
          </Box>
        </Box>
      </GridToolbarContainer>
    </>
  );
};

export { DataGridToolBar };
